// Session-authenticated JSON client for the Inertia frontend's background XHR
// (timer saves, task CRUD, calendar). Talks to /web/internal/* using the Rails
// cookie session + CSRF token (from the <meta name="csrf-token"> tag), NOT the
// Bearer token the old Next.js app used. Api::V1 stays token-only for Tauri/OpenClaw.

const API_BASE_URL = '/web/internal'

export class ApiError extends Error {
  status: number
  data: unknown
  constructor(message: string, status: number, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

// CSRF token 优先读 XSRF-TOKEN cookie，<meta> 只作兜底。顺序反过来会坏，而且
// 坏得很难认：
//
// 登录时 Web::SessionsController#create 调 reset_session，于是 session 里的
// _csrf_token 换了一个。而 <meta name="csrf-token"> 是 layouts/inertia.html.erb
// 渲染整页时写进 <head> 的，Inertia 的 XHR 导航（含登录后跟随 302 那一跳）只换
// 页面组件、不重新渲染 <head> —— 所以登录之后页面上那个 meta token 仍然属于
// 登录前那个已经作废的 session。带着它发写请求，Rails 回
// `ActionController::InvalidAuthenticityToken` + 422，而那条消息与「Origin 不
// 匹配」共用，看着像凭证坏了或跨域问题。硬刷新一次就好（整页重渲染 → meta 变新），
// 于是它表现得像个幽灵 bug。
//
// XSRF-TOKEN cookie 没有这个问题：Web::Internal::BaseController 的 after_action
// set_csrf_cookie 在每个响应上重写它，reset_session 之后第一个请求就是新值。
// 那个 after_action 本来就是为此存在的（Inertia 自带的 axios 也读它）。
//
// 实测（不登录也能验，CSRF 检查跑在 require_login! 之前，过了是 401）：
//   同一个 meta token，reset_session 之前 → 401 通过；之后 → 422 失败。
//
// 注意 cookie 值是 URL 编码过的（token 是 base64，含 + / =），必须解码。
function csrfToken(): string {
  if (typeof document === 'undefined') return ''

  const fromCookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith('XSRF-TOKEN='))
  if (fromCookie) return decodeURIComponent(fromCookie.slice('XSRF-TOKEN='.length))

  // 兜底：整页刚加载、还没打过任何 /web/internal 端点时 cookie 尚不存在，
  // 而此刻 meta 是新鲜的。
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

type RequestOptions = { method?: string; body?: unknown; headers?: Record<string, string> }

async function handleRequest(endpoint: string, options: RequestOptions = {}) {
  const { method = 'GET', body } = options
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-CSRF-Token': csrfToken(),
    Accept: 'application/json',
    ...options.headers,
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  })

  let data: any
  const contentType = response.headers.get('content-type')
  if (contentType && contentType.includes('application/json')) {
    data = await response.json()
  } else {
    data = await response.text()
  }

  if (!response.ok) {
    throw new ApiError((data && data.error) || 'Unknown error occurred', response.status, data)
  }
  return data
}

const api = {
  auth: {
    // Returns { id, email, name, daily_completed_pomodoros } for the current session user.
    me: () => handleRequest('/me'),
  },

  projects: {
    list: () => handleRequest('/projects'),
    create: (project: Record<string, unknown>) =>
      handleRequest('/projects', { method: 'POST', body: { project } }),
    update: (id: number, project: Record<string, unknown>) =>
      handleRequest(`/projects/${id}`, { method: 'PATCH', body: { project } }),
    destroy: (id: number) => handleRequest(`/projects/${id}`, { method: 'DELETE' }),
    archive: (id: number) => handleRequest(`/projects/${id}/archive`, { method: 'POST' }),
    unarchive: (id: number) => handleRequest(`/projects/${id}/unarchive`, { method: 'POST' }),
    reorder: (order: number[]) =>
      handleRequest('/projects/reorder', { method: 'POST', body: { order } }),
  },

  plan: {
    fetch: (options: { groupBy?: string; from?: string; to?: string } = {}) => {
      const query = new URLSearchParams();
      if (options.groupBy) query.set('group_by', options.groupBy);
      if (options.from) query.set('from', options.from);
      if (options.to) query.set('to', options.to);
      const suffix = query.toString();
      return handleRequest(`/tasks/plan${suffix ? `?${suffix}` : ''}`);
    },
    backlog: () => handleRequest('/tasks/backlog'),
    updateTask: (id: number, task: Record<string, unknown>) =>
      handleRequest(`/tasks/${id}`, { method: 'PATCH', body: { task } }),
  },

  timeEntries: {
    current: () => handleRequest('/time_entries/current'),
    start: (payload: Record<string, unknown>) =>
      handleRequest('/time_entries/start', { method: 'POST', body: payload }),
    stop: () => handleRequest('/time_entries/stop', { method: 'POST' }),
    list: (range: { from?: string; to?: string; taskId?: number } = {}) => {
      const query = new URLSearchParams();
      if (range.from) query.set('from', range.from);
      if (range.to) query.set('to', range.to);
      // /todo 详情栏按任务取「时间记录」，不塞进页面 props——否则每条任务都
      // 驮着自己的计时历史
      if (range.taskId) query.set('task_id', String(range.taskId));
      const suffix = query.toString();
      return handleRequest(`/time_entries${suffix ? `?${suffix}` : ''}`);
    },
    create: (timeEntry: Record<string, unknown>) =>
      handleRequest('/time_entries', { method: 'POST', body: { time_entry: timeEntry } }),
    update: (id: number, timeEntry: Record<string, unknown>) =>
      handleRequest(`/time_entries/${id}`, { method: 'PATCH', body: { time_entry: timeEntry } }),
    destroy: (id: number) => handleRequest(`/time_entries/${id}`, { method: 'DELETE' }),
  },

  worklogs: {
    // 列表走 Inertia props，这里只有「取正文」和「删除」两件事。
    get: (id: number) => handleRequest(`/worklogs/${id}`),
    destroy: (id: number) => handleRequest(`/worklogs/${id}`, { method: 'DELETE' }),
  },

  wikiPages: {
    // 建页只收标题：这一期没有「新建页面」那种带正文的表单，列表页那个输入框
    // 敲完标题就建，正文进编辑器再写。
    create: (title: string) =>
      handleRequest('/wiki_pages', { method: 'POST', body: { title } }),
    // base_version 是进编辑态时那一版的号。服务端不一致就回 409，
    // handleRequest 会抛 ApiError（status 409，data.current_version 是当前版本）。
    //
    // body 直接摊平，不包在 { wiki_page: ... } 里 —— 控制器读的是顶层 params，
    // 与 tasks 那组的 { task } 包法不同。
    //
    // base_version 是可选的：标签编辑那个入口不传它。乐观锁保护的是「正文被
    // 覆盖」，而改标签不覆盖正文 —— 为它回 409 只会让人一脸问号（服务端那侧
    // 「不传就不检查」是同一条规矩）。
    update: (
      urlKey: string,
      payload: {
        title?: string;
        body_md?: string;
        tags?: string[];
        base_version?: number;
        auto_saved?: boolean;
      },
    ) => handleRequest(`/wiki_pages/${urlKey}`, { method: 'PATCH', body: payload }),
    // 形状与 worklogs.destroy 逐字一致。助手叫 handleRequest（不是 request），
    // 它已经处理了 API_BASE_URL（'/web/internal'）、CSRF 头和错误抛 ApiError。
    //
    // destroy 回 204、没有响应体 —— handleRequest 对此天然安全：它按
    // content-type 分流，不是 JSON 就走 response.text()，所以不会在空体上抛
    // JSON 解析错误。不需要在这里做任何特殊处理。
    destroy: (urlKey: string) => handleRequest(`/wiki_pages/${urlKey}`, { method: 'DELETE' }),
    restore: (urlKey: string) => handleRequest(`/wiki_pages/${urlKey}/restore`, { method: 'POST' }),
    // 回滚不删历史 —— 服务端拿那一版的内容写一个新版（§6.4）。
    rollback: (urlKey: string, version: number) =>
      handleRequest(`/wiki_pages/${urlKey}/rollback`, { method: 'POST', body: { version } }),
  },

  // /todo 右栏的留言。列表不进 Inertia props —— 否则每条任务都驮着自己的
  // 全部留言正文（与 timeEntries 同一个理由）。
  comments: {
    list: (taskId: number) => handleRequest(`/tasks/${taskId}/comments`),
    create: (taskId: number, bodyMd: string) =>
      handleRequest(`/tasks/${taskId}/comments`, {
        method: 'POST',
        body: { comment: { body_md: bodyMd } },
      }),
    update: (id: number, bodyMd: string) =>
      handleRequest(`/comments/${id}`, {
        method: 'PATCH',
        body: { comment: { body_md: bodyMd } },
      }),
    destroy: (id: number) => handleRequest(`/comments/${id}`, { method: 'DELETE' }),
  },

  aiPrompt: {
    // 参数名是 action_kind 不是 action —— params[:action] 被 Rails 占着，
    // 它永远是控制器动作名。传空串 = 让服务端挑默认动作。
    show: (taskId: number, action: string) =>
      handleRequest(`/tasks/${taskId}/ai_prompt?action_kind=${encodeURIComponent(action)}`),
    // 清单级。view 是 /todo/:view 里那个键；项目清单额外带 project_id。
    list: (view: string, projectId: number | null, action: string) =>
      handleRequest(
        `/list_ai_prompt?view=${encodeURIComponent(view)}` +
          `${projectId ? `&project_id=${projectId}` : ''}` +
          `&action_kind=${encodeURIComponent(action)}`
      ),
  },

  // 「串起来」服务端直接执行版。只有单条级任务有，清单级没有这条通道——
  // 见 config/routes.rb 里 worklog_link 那条注释。
  worklogLink: {
    create: (taskId: number) =>
      handleRequest(`/tasks/${taskId}/worklog_link`, { method: 'POST' }),
  },

  // 「串起来」批量版的状态胶囊——作用对象是 current_user 本人，不挂在某条
  // 任务或某个清单下面（asaptodo#472）。
  worklogLinkRun: {
    show: () => handleRequest('/worklog_link_run'),
    create: () => handleRequest('/worklog_link_run', { method: 'POST' }),
  },

  // 图片上传走 multipart，不能用 handleRequest —— 那个函数会把 body 当 JSON
  // 序列化并写死 Content-Type。这里只手动补 CSRF 头，其余交给浏览器（它会
  // 自己带上 multipart 的 boundary）。
  taskImages: {
    create: async (taskId: number, file: File): Promise<{ url: string }> => {
      const form = new FormData();
      form.append('file', file);

      const response = await fetch(`${API_BASE_URL}/tasks/${taskId}/images`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: form,
        credentials: 'include',
      });

      const data = await response.json();
      if (!response.ok) {
        throw new ApiError(data?.error || '图片传不上去', response.status, data);
      }
      return data;
    },
  },

  // 与 taskImages.create 同构：走 multipart，所以不能用 handleRequest（那个函数
  // 会把 body 当 JSON 序列化并写死 Content-Type）。
  //
  // 这一处确实照 taskImages.create 写了一份，而那不违反 §8.1 的「零处复刻」：
  // 不能复制的是校验、image_url_for、以及 useMarkdownImages 里那两个不显然的
  // 地方 —— 这 12 行是 fetch 的样板，没有承载任何论证。
  wikiImages: {
    create: async (urlKey: string, file: File): Promise<{ url: string }> => {
      const form = new FormData();
      form.append('file', file);

      const response = await fetch(`${API_BASE_URL}/wiki_pages/${urlKey}/images`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: form,
        credentials: 'include',
      });

      const data = await response.json();
      if (!response.ok) {
        throw new ApiError(data?.error || '图片传不上去', response.status, data);
      }
      return data;
    },
  },

  settings: {
    // Returns camelCase settings ({ workDuration, breakDuration, ... }) as the
    // PomodoroContext expects.
    get: () => handleRequest('/settings'),
  },

  tasks: {
    getAll: () => handleRequest('/tasks'),
    getTodoList: () => handleRequest('/tasks/todo_list'),
    getCompletedToday: () => handleRequest('/tasks/completed_today'),
    getCategories: () => handleRequest('/tasks/categories'),
    get: (id: number) => handleRequest(`/tasks/${id}`),
    create: (task: Record<string, unknown>) => handleRequest('/tasks', { method: 'POST', body: { task } }),
    update: (id: number, task: Record<string, unknown>) => handleRequest(`/tasks/${id}`, { method: 'PUT', body: { task } }),
    updatePosition: (id: number, position: number) => handleRequest(`/tasks/${id}`, { method: 'PUT', body: { position } }),
    // delete 是软删（进回收站），restore 是它的反向操作。
    delete: (id: number) => handleRequest(`/tasks/${id}`, { method: 'DELETE' }),
    restore: (id: number) => handleRequest(`/tasks/${id}/restore`, { method: 'POST' }),
    reorder: (taskIds: number[]) => handleRequest('/tasks/reorder', { method: 'POST', body: { order: taskIds } }),
    // 「挂一条已有任务」的候选。只读；挂上/解绑走 update(parent_id)。
    linkable: (q: string, under: number) =>
      handleRequest(`/tasks/linkable?q=${encodeURIComponent(q)}&under=${under}`),
  },

  pomodoros: {
    getAll: () => handleRequest('/pomodoros'),
    get: (id: string) => handleRequest(`/pomodoros/${id}`),
    create: (pomodoro: Record<string, unknown>) => handleRequest('/pomodoros', { method: 'POST', body: { pomodoro } }),
    update: (id: string, pomodoro: Record<string, unknown>) => handleRequest(`/pomodoros/${id}`, { method: 'PUT', body: { pomodoro } }),
    delete: (id: string) => handleRequest(`/pomodoros/${id}`, { method: 'DELETE' }),
  },
}

export default api
